package com.evoapps.evoshift.ui.adaptive

import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.widgets.ProvideDialogColors
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.foundation.focusable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.lifecycle.ViewModelStore
import androidx.lifecycle.ViewModelStoreOwner
import androidx.lifecycle.viewmodel.compose.LocalViewModelStoreOwner
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import com.evoapps.evoshift.navigation.DetailPanes
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.LightMetallicBrush
import androidx.compose.foundation.border
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import org.jetbrains.compose.resources.stringResource

/*
 * SH-105: wide windows reorganise screens instead of stretching the phone column. A list (people,
 * absences, notifications, days) and the chosen item's details sit side by side on expanded and
 * wider windows (tablets in landscape, a flat iPhone Duo, Googlebook and desktop windows, the
 * web); phones and medium windows keep their own flow (a new screen, a sheet or a dialog).
 *
 * The chosen item is the screen's state, not a route: deep links open the same screens as before,
 * and back (the system's, Esc, the desktop's View > Back and the browser's) closes the pane first.
 */

/** Test tags of the panes, for UI tests and robots. */
object PaneTags {
    /** The list (or grid, or table) beside the detail pane. */
    const val LIST = "pane_list"
    /** The detail pane: the chosen item, or [PLACEHOLDER] while nothing is chosen. */
    const val DETAIL = "pane_detail"
    /** What the detail pane says while nothing is chosen. */
    const val PLACEHOLDER = "pane_placeholder"
    /** A third, supporting pane on large windows (e.g. the charts beside the daily records). */
    const val SUPPORTING = "pane_supporting"
    /** A dashboard's column, numbered from 0: "dashboard_column_0". */
    const val DASHBOARD_COLUMN = "dashboard_column_"
    /** A detail pane's close button. */
    const val CLOSE = "pane_close"
}

/**
 * Whether this window shows a list and the chosen item's details side by side: expanded or wider,
 * and not as short as a phone in landscape (that keeps its sheets and dialogs).
 */
val WindowWidthClass.showsDetailPane: Boolean
    @Composable get() = isExpanded && !LocalWindowHeightClass.current.isCompact

/** The list's share of the width beside a detail pane: narrower on laptop-wide windows. */
private fun WindowWidthClass.listWeight(): Float = if (isLarge) 0.38f else 0.45f

/**
 * A list and the chosen item's details. On expanded and wider windows: [list] on the left and,
 * on the right, [detail] when [selected] (back and Esc call [onClose]) or [placeholder] while
 * nothing is chosen. Narrower: only [list]; the screen opens its item as it always did.
 *
 * The detail pane measures its own width class (LocalWindowWidthClass), so a screen shown inside
 * it lays out for the pane, not for the whole window.
 *
 * SH-130: the single list fills the screen and centres its own content (e.g. AdaptiveCardGrid's
 * `maxWidth`), so the wheel scrolls it from the empty sides too.
 *
 * SH-136: side by side, the two panes fill the window too: capping them ([maxWidth], as before)
 * left dead margins on very wide windows, where the wheel scrolled nothing. Every pixel now belongs
 * to a pane that scrolls; each pane's content keeps its own width (the lists' `maxWidth`,
 * [DetailPane]'s [DetailPaneMaxWidth], the detail screens' `contentWidth`). [maxWidth] stays for
 * the callers, which pass the same width to their lists.
 */
@Composable
fun ListDetailLayout(
    selected: Boolean,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    maxWidth: Dp = Dp.Unspecified,
    placeholder: @Composable () -> Unit = {},
    list: @Composable () -> Unit,
    detail: @Composable () -> Unit
) {
    val widthClass = LocalWindowWidthClass.current
    if (!widthClass.showsDetailPane) {
        Box(modifier.fillMaxSize()) { list() }
        return
    }
    Row(modifier.fillMaxSize()) {
        Box(Modifier.weight(widthClass.listWeight()).fillMaxHeight().testTag(PaneTags.LIST)) { list() }
        PaneDivider()
        Box(Modifier.weight(1f - widthClass.listWeight()).fillMaxHeight().testTag(PaneTags.DETAIL)) {
            if (selected) {
                DetailPaneBack(onClose = onClose)
                ProvideWindowWidthClass(Modifier.fillMaxSize(), posture = DevicePosture.Normal) { detail() }
            } else {
                Box(Modifier.fillMaxSize().testTag(PaneTags.PLACEHOLDER)) { placeholder() }
            }
        }
    }
}

/** SH-136: the widest a [DetailPane]'s content gets (the pane itself fills its share of the window). */
val DetailPaneMaxWidth = 760.dp

/** The thin line between two panes. */
@Composable
fun PaneDivider(modifier: Modifier = Modifier) {
    Box(modifier.fillMaxHeight().width(1.dp).background(DeepNavy.copy(alpha = 0.1f)))
}

/**
 * While a detail pane is open: back closes it instead of leaving the screen. The system back
 * (Android, iOS' swipe), and through [DetailPanes] the desktop's View > Back and the browser's
 * back button.
 */
@Composable
fun DetailPaneBack(enabled: Boolean = true, onClose: () -> Unit) {
    val close by rememberUpdatedState(onClose)
    NavigationBackHandler(
        state = rememberNavigationEventState(NavigationEventInfo.None),
        isBackEnabled = enabled,
        onBackCompleted = { close() }
    )
    if (enabled) {
        DisposableEffect(Unit) {
            val token = DetailPanes.opened { close() }
            onDispose { DetailPanes.closed(token) }
        }
    }
}

/** SH-128: the corner radius of a floating detail pane (the app's cards). */
val DetailPaneShape = RoundedCornerShape(20.dp)

/** SH-128: the margin around a floating detail pane: from the window's edges and the list beside it. */
val DetailPaneMargin = 12.dp

/**
 * A detail pane's frame: [title] with an icon and a close button over [content], which scrolls.
 * [dark]: the navy look of the app's sheets and dialogs (their content reads on it unchanged);
 * light: the cream of the app's cards. SH-128: it floats as a card, every corner rounded, with a
 * margin on every side. It takes the focus when it opens, so Esc closes it on a laptop.
 */
@Composable
fun DetailPane(
    title: String,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null,
    subtitle: String? = null,
    dark: Boolean = true,
    scrollable: Boolean = true,
    tag: String? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    val focus = remember { FocusRequester() }
    LaunchedEffect(focus) { runCatching { focus.requestFocus() } }
    val close by rememberUpdatedState(onClose)
    val onColor = if (dark) Color.White else DeepNavy
    val accent = if (dark) LightGold else DeepNavy
    Column(
        modifier
            .padding(DetailPaneMargin)
            .shadow(if (dark) 8.dp else 4.dp, DetailPaneShape)
            .clip(DetailPaneShape)
            .background(if (dark) DarkMetallicBackgroundBrush else LightMetallicBrush)
            .border(1.dp, if (dark) LightGold.copy(alpha = 0.35f) else DeepNavy.copy(alpha = 0.15f), DetailPaneShape)
            .onKeyEvent { event ->
                if (event.type == KeyEventType.KeyDown && event.key == Key.Escape) {
                    close()
                    true
                } else false
            }
            .focusRequester(focus)
            .focusable()
            .then(if (tag != null) Modifier.testTag(tag) else Modifier)
    ) {
        Row(
            Modifier.fillMaxWidth().padding(start = 20.dp, end = 8.dp, top = 12.dp, bottom = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (icon != null) {
                Icon(icon, contentDescription = null, tint = accent, modifier = Modifier.size(20.dp))
                Spacer(Modifier.width(12.dp))
            }
            Column(Modifier.weight(1f)) {
                Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = accent)
                if (subtitle != null) {
                    Text(subtitle, style = MaterialTheme.typography.bodySmall, color = onColor.copy(alpha = 0.7f))
                }
            }
            IconButtonWithTooltip(onClick = onClose, modifier = Modifier.handCursor().testTag(PaneTags.CLOSE), tooltip = stringResource(Res.string.close)) {
                Icon(Icons.Default.Close, contentDescription = stringResource(Res.string.close), tint = onColor)
            }
        }
        // The sheets' and dialogs' content reads its default text colour from here.
        DetailPaneColors(dark) {
            Column(
                Modifier
                    .fillMaxSize()
                    .then(if (scrollable) Modifier.verticalScroll(rememberScrollState()) else Modifier)
                    // SH-136: the pane may be very wide; its content keeps a readable width and
                    // the scroll (before this) still answers the wheel over the sides.
                    .contentWidth(DetailPaneMaxWidth)
                    .padding(start = 20.dp, end = 20.dp, top = 8.dp, bottom = 32.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
                content = content
            )
        }
    }
}

/** The detail pane while nothing is chosen: a quiet hint where the details will show. */
@Composable
fun PanePlaceholder(text: String, icon: ImageVector? = null, modifier: Modifier = Modifier) {
    Column(
        modifier.fillMaxSize().padding(32.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        if (icon != null) {
            Icon(icon, contentDescription = null, tint = DeepNavy.copy(alpha = 0.3f), modifier = Modifier.size(48.dp))
            Spacer(Modifier.size(12.dp))
        }
        Text(text, style = MaterialTheme.typography.bodyLarge, color = DeepNavy.copy(alpha = 0.55f), textAlign = TextAlign.Center)
    }
}

/**
 * View models of what a detail pane shows (diViewModel inside it) live as long as the pane shows
 * that item, not as long as the screen: choosing another person clears the last one's (and its
 * Firestore listeners). On phones the same screen is a route with its own store.
 */
@Composable
fun PaneViewModelStore(key: Any, content: @Composable () -> Unit) {
    val owner = remember(key) {
        object : ViewModelStoreOwner {
            override val viewModelStore = ViewModelStore()
        }
    }
    DisposableEffect(owner) { onDispose { owner.viewModelStore.clear() } }
    CompositionLocalProvider(LocalViewModelStoreOwner provides owner, content = content)
}

/**
 * SH-105: dashboard cards in columns that read well side by side (e.g. today and the to-dos on
 * the left, the charts on the right). Each inner list is one column, top to bottom; empty columns
 * are left out. Phones use their own single column instead.
 */
@Composable
fun DashboardColumns(
    columns: List<List<@Composable () -> Unit>>,
    modifier: Modifier = Modifier,
    spacing: Dp = 16.dp,
    weights: List<Float>? = null
) {
    val shown = columns.withIndex().filter { it.value.isNotEmpty() }
    Row(modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(spacing), verticalAlignment = Alignment.Top) {
        shown.forEachIndexed { position, (index, sections) ->
            Column(
                Modifier.weight(weights?.getOrNull(index) ?: 1f).testTag(PaneTags.DASHBOARD_COLUMN + position),
                verticalArrangement = Arrangement.spacedBy(spacing)
            ) {
                sections.forEach { it() }
            }
        }
    }
}

/** A dark pane's content in the dialogs' colours; a light one's in the screen's navy. */
@Composable
private fun DetailPaneColors(dark: Boolean, content: @Composable () -> Unit) {
    if (dark) ProvideDialogColors(content) else CompositionLocalProvider(LocalContentColor provides DeepNavy, content = content)
}
